import React, { useCallback, useEffect, useState } from "react";
import { Download, IndianRupee, Loader2, PiggyBank, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { downloadCSV, fdate, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState, LoadingRows } from "@/components/common/EmptyState";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProfitDashboard } from "./ProfitDashboard";
import { ProfitTrail } from "./ProfitTrail";
import { DirectCosts } from "./DirectCosts";
import { ProfitSettings } from "./ProfitSettings";

export const pct = (v) => `${Number(v || 0).toFixed(1)}%`;

export default function Profitability() {
  const { can, isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ categories: [], config: {} });
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [trailId, setTrailId] = useState(null);
  const [tab, setTab] = useState("dashboard");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/profit/invoices", { params: { month } });
      setRows(data.rows);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/profit/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const shown = rows.filter((r) => !q
    || `${r.number} ${r.customer_name} ${r.salesperson}`.toLowerCase().includes(q.toLowerCase()));
  const revenue = rows.reduce((a, r) => a + r.revenue, 0);
  const cost = rows.reduce((a, r) => a + r.total_cost, 0);
  const profit = revenue - cost;

  const columns = [
    {
      key: "invoice", label: "Invoice",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{r.customer_name}</p>
          <p className="num truncate text-xs text-muted-foreground">
            {r.number} &middot; {fdate(r.date)}{r.salesperson ? ` \u00b7 ${r.salesperson}` : ""}
          </p>
        </div>
      ),
    },
    { key: "revenue", label: "Net revenue", align: "right", render: (r) => <span className="num text-sm">{money(r.revenue)}</span> },
    { key: "cogs", label: "Goods", align: "right", render: (r) => <span className="num text-sm text-muted-foreground">{money(r.cogs)}</span>, hideMobile: true },
    { key: "freight", label: "Freight", align: "right", render: (r) => <span className="num text-sm text-muted-foreground">{money(r.freight)}</span>, hideMobile: true },
    { key: "expenses", label: "Expenses", align: "right", render: (r) => <span className="num text-sm text-muted-foreground">{money(r.expenses)}</span>, hideMobile: true },
    { key: "employee", label: "People", align: "right", render: (r) => <span className="num text-sm text-muted-foreground">{money(r.employee)}</span>, hideMobile: true },
    { key: "cost", label: "Total cost", align: "right", render: (r) => <span className="num text-sm">{money(r.total_cost)}</span>, hideMobile: true },
    {
      key: "profit", label: "Profit", align: "right",
      render: (r) => (
        <span className={`num text-sm font-bold ${r.profit < 0 ? "text-destructive" : "text-success"}`}>
          {money(r.profit)}
        </span>
      ),
    },
    {
      key: "margin", label: "Margin", align: "right",
      render: (r) => (
        <span className="flex items-center justify-end gap-1.5">
          {r.overridden?.length > 0 && (
            <span className="rounded-full bg-warning-soft px-1.5 py-0.5 text-[10px] font-semibold text-warning"
              title="Some costs were corrected by hand">edited</span>
          )}
          <span className={`num text-sm font-semibold ${r.margin < 0 ? "text-destructive"
            : r.margin < (meta.config?.thin_margin_pct ?? 15) ? "text-warning" : "text-foreground"}`}>
            {pct(r.margin)}
          </span>
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={PiggyBank}
        title="Profitability"
        subtitle="What each invoice really earned after goods, freight, customer expenses and the time your team spent."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="w-[170px]" data-testid="profit-month" />
            {rows.length > 0 && can("profitability", "export") && (
              <Button variant="outline" data-testid="profit-export-btn" onClick={() => {
                downloadCSV(rows.map((r) => ({
                  Invoice: r.number, Date: r.date, Customer: r.customer_name,
                  Salesperson: r.salesperson, NetRevenue: r.revenue, Goods: r.cogs,
                  Freight: r.freight, Expenses: r.expenses, EmployeeCost: r.employee,
                  OtherCost: r.other, TotalCost: r.total_cost, GrossProfit: r.gross_profit,
                  Profit: r.profit, MarginPct: r.margin, Corrected: (r.overridden || []).join("/"),
                })), `profitability-${month}.csv`);
                toast.success("Exported");
              }}>
                <Download className="h-4 w-4" /> Export
              </Button>
            )}
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="profit-kpis">
        <StatCard label="Net revenue" value={revenue} currency icon="IndianRupee" tone="info" testId="kpi-profit-revenue" />
        <StatCard label="Direct cost" value={cost} currency icon="Package" tone="warning" testId="kpi-profit-cost" />
        <StatCard label="Profit" value={profit} currency icon="TrendingUp"
          tone={profit < 0 ? "danger" : "success"} testId="kpi-profit-amount" />
        <StatCard label="Margin" value={revenue > 0 ? `${(100 * profit / revenue).toFixed(1)}%` : "0%"}
          icon="Percent" tone="primary" testId="kpi-profit-margin"
          hint={`${rows.length} invoice(s) in ${month}`} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="scroll-slim -mx-1 max-w-full overflow-x-auto px-1">
          <TabsList className="w-max">
            <TabsTrigger value="dashboard" data-testid="profit-tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="profit-tab-invoices">Invoices</TabsTrigger>
            {can("profitability", "edit") && (
              <TabsTrigger value="costs" data-testid="profit-tab-costs">Direct costs</TabsTrigger>
            )}
            {isAdmin && <TabsTrigger value="settings" data-testid="profit-tab-settings">Settings</TabsTrigger>}
          </TabsList>
        </div>

        <TabsContent value="dashboard" className="mt-5">
          <ProfitDashboard month={month} onOpenInvoice={setTrailId} />
        </TabsContent>

        <TabsContent value="invoices" className="mt-5 space-y-4">
          <div className="relative max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="profit-search"
              placeholder="Search invoice, customer or salesperson" />
          </div>
          {loading ? <LoadingRows /> : (
            <DataTable columns={columns} rows={shown} onRowClick={(r) => setTrailId(r.invoice_id)}
              rowTestId={(r) => `profit-row-${r.number}`}
              rowClassName={(r) => r.profit < 0 && "bg-destructive-soft/25"}
              empty={<EmptyState icon={IndianRupee} title="No invoices in this month"
                description="Pick another month, or raise an invoice and its profit appears here on its own." />} />
          )}
          <p className="text-xs text-muted-foreground">
            Tap any row to see the full cost trail{can("profitability", "edit") ? " and correct an allocation" : ""}.
          </p>
        </TabsContent>

        <TabsContent value="costs" className="mt-5">
          <DirectCosts month={month} categories={meta.categories || []} onChanged={load} />
        </TabsContent>

        <TabsContent value="settings" className="mt-5">
          <ProfitSettings config={meta.config} onSaved={(c) => { setMeta((m) => ({ ...m, config: c })); load(); }} />
        </TabsContent>
      </Tabs>

      <ProfitTrail invoiceId={trailId} open={!!trailId} onOpenChange={(v) => !v && setTrailId(null)}
        onChanged={load} />
    </div>
  );
}
